import {
  Avatar,
  Box,
  Button,
  capitalize,
  Checkbox,
  Container,
  Grid,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { STRINGS_LOAD_MORE } from "../../../data/strings/Strings";
import { HeaderView } from "../../component/HeaderView";
import { OrdersViewModel } from "./OrdersViewModel";
import { LoadingView } from "../../component/LoadingView";
import { EmptyView } from "../../component/EmptyView";
import {
  provideDownloadMultipleOrderReceiptsUseCase,
  provideGetAllOrdersUseCase,
  provideGetAllTransactionsUseCase,
  provideUpdateOrderUseCase,
} from "../../di/UseCasesModule";
import { CustomTextView } from "../../component/CustomTextView";
import {
  COLOR_BLACK,
  COLOR_GREEN,
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_ORANGE,
  COLOR_RED,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
  GRADIENT_MAIN,
} from "../../../data/colors/Colors";
import moment from "moment";
import { Link } from "react-router-dom";
import { ROUTE_ENDPOINTS } from "../../../data/constant/RouteConstants";
import { Order, OrderStatus } from "../../../domain/model/Order";
import { MAIN_FONT_FAMILY } from "../../../data/constant/Constants";
import { CustomImageView } from "../../component/CustomImageView";
import {
  CancelRounded,
  CheckBox,
  CheckCircleRounded,
  CheckRounded,
  DownloadRounded,
  PendingActions,
  PendingActionsRounded,
  PendingRounded,
} from "@mui/icons-material";

export function OrdersView() {
  const viewModel = OrdersViewModel(
    provideGetAllOrdersUseCase(),
    provideUpdateOrderUseCase(),
    provideDownloadMultipleOrderReceiptsUseCase()
  );

  return (
    <Stack spacing={2}>
      <HeaderView
        title={"Orders"}
        isLayoutFullWidthInSmallerScreens={false}
        layout={
          <Stack spacing={1} direction={"row"} justifyContent={"end"}>
            <Button
              onClick={viewModel.downloadMultipleOrderReceipts}
              variant="outlined"
              sx={{
                borderRadius: 22,
                borderColor: COLOR_BLACK,
                "&:hover": {
                  borderColor: COLOR_BLACK,
                },
              }}
            >
              <DownloadRounded fontSize="medium" sx={{ color: COLOR_BLACK }} />
              <CustomTextView
                text={`Download Receipts (${
                  viewModel.orders?.filter((o) => o.selected).length
                })`}
                fontWeight="bold"
                fontSize={16}
                props={{ textTransform: "none" }}
                textColor={COLOR_BLACK}
              />
            </Button>
          </Stack>
        }
      />
      <Container maxWidth={false}>
        {viewModel.isLoading && viewModel.orders?.length === 0 ? (
          <LoadingView />
        ) : (
          <>
            <Grid container spacing={2}>
              {viewModel.orders && viewModel.orders.length > 0 ? (
                <OrdersTableView
                  orders={viewModel.orders}
                  hasCustomer
                  onStatusChanged={(id, status) => {
                    viewModel.updateOrder(id, { status });
                  }}
                  selectUnselectOrder={viewModel.selectUnselectOrder}
                />
              ) : (
                <EmptyView />
              )}
              <Grid
                alignItems={"center"}
                justifyContent={"center"}
                display={"flex"}
                size={12}
              >
                {viewModel.isLoading ? (
                  <LoadingView minHeight="5vh" />
                ) : (
                  <>
                    {viewModel.showLoadMoreButton && (
                      <Button
                        variant="outlined"
                        sx={{
                          borderRadius: 22,
                          textTransform: "none",
                          borderColor: COLOR_BLACK,
                          "&:hover": {
                            borderColor: COLOR_BLACK,
                            background: COLOR_LIGHT_GRAY,
                          },
                        }}
                        onClick={viewModel.getOrders}
                      >
                        <CustomTextView
                          text={STRINGS_LOAD_MORE}
                          variant="h6"
                          fontWeight="bold"
                        />
                      </Button>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </Stack>
  );
}

export function OrdersTableView({
  orders,
  hasCustomer,
  onStatusChanged,
  selectUnselectOrder,
}: {
  orders: Array<Order>;
  hasCustomer: boolean;
  onStatusChanged: (orderId: string, status: OrderStatus) => void;
  selectUnselectOrder: (orderId: string) => void;
}) {
  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 8,
        background: "transparent",
        border: "1px solid lightGray",
        boxShadow: 0,
      }}
    >
      <Table aria-label="simple table">
        <TableHead sx={{ background: COLOR_MAIN }}>
          <TableRow>
            <TableCell>
              <CustomTextView
                text="Id"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
              />
            </TableCell>
            {hasCustomer && (
              <TableCell>
                <CustomTextView
                  text="Customer"
                  fontWeight="bold"
                  fontSize={14}
                  textColor="white"
                />
              </TableCell>
            )}
            <TableCell>
              <CustomTextView
                text="Status"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
              />
            </TableCell>
            <TableCell>
              <CustomTextView
                text="Created"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
              />
            </TableCell>
            <TableCell>
              <CustomTextView
                text="Price"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
              />
            </TableCell>
            <TableCell>
              <CustomTextView
                text="Delivery Price"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
              />
            </TableCell>
            <TableCell align="center">
              <CustomTextView
                text="Quantity"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
                textAlign="center"
              />
            </TableCell>
            <TableCell align="center">
              <CustomTextView
                text="Product"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
                textAlign="center"
              />
            </TableCell>
            <TableCell align="center">
              <CustomTextView
                text="Details"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
                textAlign="center"
              />
            </TableCell>
            <TableCell align="center">
              <CustomTextView
                text="Selected"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
                textAlign="center"
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order) => (
            <TableRow
              key={order._id}
              sx={{
                "&:last-child td, &:last-child th": { border: 0 },
              }}
            >
              <TableCell>
                <CustomTextView text={`#${order.orderId}`} fontSize={14} />
              </TableCell>
              {hasCustomer && (
                <TableCell>
                  {order.customer && (
                    <Stack direction={"row"} alignItems={"center"} spacing={1}>
                      <Avatar
                        src={order.customer.profileImage}
                        sx={{ width: 60, height: 60 }}
                      />
                      <Stack>
                        <CustomTextView
                          text={order.customer.name}
                          fontSize={16}
                          fontWeight="bold"
                        />
                        <Link
                          to={
                            ROUTE_ENDPOINTS.HOME_CUSTOMERS +
                            "/" +
                            order.customer._id
                          }
                          target="_blank"
                        >
                          <CustomTextView
                            text={order.customer.user?.email}
                            fontSize={14}
                          />
                        </Link>
                      </Stack>
                    </Stack>
                  )}
                </TableCell>
              )}
              <TableCell>
                <Select
                  sx={{
                    fontFamily: MAIN_FONT_FAMILY,
                    borderRadius: 4,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor:
                        order.status == OrderStatus.CONFIRMED
                          ? COLOR_GREEN
                          : order.status == OrderStatus.PENDING
                          ? COLOR_ORANGE
                          : COLOR_RED,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLOR_BLACK,
                    },
                    "& .MuiSvgIcon-root": {
                      color:
                        order.status == OrderStatus.CONFIRMED
                          ? COLOR_GREEN
                          : order.status == OrderStatus.PENDING
                          ? COLOR_ORANGE
                          : COLOR_RED,
                    },
                  }}
                  fullWidth
                  inputProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          background: COLOR_WHITE,
                        },
                      },
                    },
                  }}
                  value={order.status}
                  onChange={(e: SelectChangeEvent<string>) => {
                    if (e.target.value == order.status) return;
                    onStatusChanged(order._id, e.target.value as OrderStatus);
                  }}
                  renderValue={(val) => (
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      spacing={0.5}
                      width={"100%"}
                    >
                      {order.status == OrderStatus.CONFIRMED && (
                        <CheckCircleRounded
                          fontSize="small"
                          sx={{ color: COLOR_GREEN }}
                        />
                      )}
                      {order.status == OrderStatus.PENDING && (
                        <PendingActionsRounded
                          fontSize="small"
                          sx={{ color: "orange" }}
                        />
                      )}
                      {order.status == OrderStatus.CANCELLED && (
                        <CancelRounded fontSize="small" sx={{ color: "red" }} />
                      )}
                      <CustomTextView
                        text={capitalize(val as string)}
                        variant="body2"
                        textColor={
                          order.status === OrderStatus.CONFIRMED
                            ? COLOR_GREEN
                            : order.status === OrderStatus.PENDING
                            ? COLOR_ORANGE
                            : COLOR_RED
                        }
                      />
                    </Stack>
                  )}
                >
                  {Object.values(OrderStatus).map((status, index) => {
                    return (
                      <MenuItem
                        key={index}
                        sx={{
                          fontFamily: MAIN_FONT_FAMILY,
                        }}
                        value={status}
                      >
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          spacing={0.5}
                          width={"100%"}
                        >
                          {status == OrderStatus.CONFIRMED && (
                            <CheckCircleRounded
                              fontSize="small"
                              sx={{ color: COLOR_GREEN }}
                            />
                          )}
                          {status == OrderStatus.PENDING && (
                            <PendingActionsRounded
                              fontSize="small"
                              sx={{ color: "orange" }}
                            />
                          )}
                          {status == OrderStatus.CANCELLED && (
                            <CancelRounded
                              fontSize="small"
                              sx={{ color: "red" }}
                            />
                          )}
                          <CustomTextView
                            text={capitalize(status)}
                            variant="body2"
                          />
                        </Stack>
                      </MenuItem>
                    );
                  })}
                </Select>
              </TableCell>
              <TableCell>
                <CustomTextView
                  text={moment(new Date(order.createdAt)).fromNow()}
                  fontSize={14}
                />
              </TableCell>
              <TableCell>
                <CustomTextView
                  text={`${(order.transaction.priceInQAR - order.transaction.deliveryPrice).toFixed(2)} QR`}
                  fontSize={16}
                  fontWeight="bold"
                />
              </TableCell>
              <TableCell>
                <CustomTextView
                  text={`${order.transaction.deliveryPrice.toFixed(2)} QR`}
                  fontSize={16}
                  fontWeight="bold"
                />
              </TableCell>
              <TableCell align="center">
                <CustomTextView
                  text={`${order.qty}`}
                  fontSize={18}
                  textAlign="center"
                  fontWeight="bold"
                />
              </TableCell>
              <TableCell align="center">
                {order.product ? (
                  <Stack alignItems={"center"} spacing={1}>
                    <CustomImageView
                      width={50}
                      height={50}
                      src={
                        (
                          order.product.imagesList![
                            order.product.thumbnailIndex!
                          ] as any
                        ).downloadUrl
                      }
                      scale="contain"
                      props={{ borderRadius: 4, background: COLOR_WHITE }}
                    />
                    <Link
                      to={
                        ROUTE_ENDPOINTS.HOME_PRODUCTS + "/" + order.product._id
                      }
                      target="_blank"
                    >
                      <CustomTextView
                        text={order.product.name}
                        fontSize={14}
                        textAlign="center"
                      />
                    </Link>
                  </Stack>
                ) : (
                  <CustomTextView
                    text="REMOVED"
                    textColor="red"
                    textAlign="center"
                  />
                )}
              </TableCell>

              <TableCell>
                <Link
                  to={ROUTE_ENDPOINTS.HOME_ORDERS + "/" + order._id}
                  target="_blank"
                >
                  <CustomTextView
                    text="Details"
                    textColor="blue"
                    textAlign="center"
                  />
                </Link>
              </TableCell>
              <TableCell align="center">
                <Checkbox
                  checked={order.selected}
                  onChange={() => {
                    selectUnselectOrder(order._id);
                  }}
                  sx={{
                    "&.Mui-checked": {
                      color: COLOR_MAIN,
                    },
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
